// Use the global sqlite3Worker1Promiser from the public script
// This avoids the Node.js module resolution issue in Next.js
type SqliteBindValue =
  | string
  | number
  | boolean
  | null
  | Uint8Array
  | ArrayBuffer
type SqliteRow = Record<string, unknown>
type SqliteResponse = {
  type?: string
  result?: {
    message?: string
    resultRows?: SqliteRow[]
  }
}
type SqlitePromiser = (
  action: string,
  args?: Record<string, unknown>
) => Promise<SqliteResponse>

declare global {
  interface Window {
    sqlite3Worker1Promiser: (config: {
      onready?: () => void
      worker?: () => Worker
      onerror?: (err: unknown) => void
    }) => SqlitePromiser
  }
}

// Helper to get sqlite3Worker1Promiser, waiting if needed
function getSqlite3Worker1Promiser() {
  if (typeof window === 'undefined') {
    throw new Error('sqlite3Worker1Promiser is only available in the browser')
  }
  return window.sqlite3Worker1Promiser
}

type OpfsWriterRequest =
  | { id: number; action: 'open'; payload: { fileName: string } }
  | {
      id: number
      action: 'write'
      payload: { chunk: ArrayBuffer; byteOffset: number; byteLength: number }
    }
  | { id: number; action: 'close' }
  | { id: number; action: 'abort' }

type OpfsWriterResponse =
  | { id: number; success: true; data?: { mode?: 'sync' | 'async' } }
  | { id: number; success: false; error: string }

class OpfsWriterClient {
  private readonly fileName: string
  private worker: Worker
  private nextId = 0
  private pending = new Map<
    number,
    { resolve: (value: unknown) => void; reject: (error: Error) => void }
  >()
  private closed = false
  private ready: Promise<void>

  private constructor(fileName: string) {
    this.fileName = fileName
    // For Next.js, we need to use a path that works in both dev and production
    // The worker file will be served from public/workers/ after build
    // In development, Next.js dev server will serve it from public/
    // In production (static export), it will be in the out/ directory
    const workerPath =
      typeof window !== 'undefined'
        ? '/workers/opfs-writer.worker.js?isolation=1'
        : '' // Will never be used on server
    this.worker = new Worker(workerPath, { type: 'module' })
    this.worker.onmessage = (event: MessageEvent<OpfsWriterResponse>) => {
      const message = event.data
      const pending = this.pending.get(message.id)
      if (!pending) return
      this.pending.delete(message.id)
      if (message.success) {
        pending.resolve(message.data)
      } else {
        pending.reject(new Error(message.error))
      }
    }
    this.worker.onerror = (event) => {
      this.failAll(
        new Error(event.message || 'Unexpected OPFS writer worker error')
      )
    }
    this.worker.onmessageerror = () => {
      this.failAll(new Error('Failed to deserialize message from OPFS worker'))
    }
    this.ready = this.call('open', { fileName: this.fileName }).then(() => {})
  }

  static async create(fileName: string) {
    const client = new OpfsWriterClient(fileName)
    await client.ready
    return client
  }

  async write(chunk: Uint8Array) {
    await this.ready
    if (this.closed) {
      throw new Error('OPFS writer already closed')
    }
    const payload = {
      chunk: chunk.buffer,
      byteOffset: chunk.byteOffset,
      byteLength: chunk.byteLength
    }
    await this.call('write', payload, [chunk.buffer])
  }

  async close() {
    if (this.closed) return
    try {
      await this.call('close')
    } finally {
      this.dispose()
    }
  }

  async abort() {
    if (this.closed) return
    try {
      await this.call('abort').catch(() => {})
    } finally {
      this.dispose()
    }
  }

  private dispose() {
    if (this.closed) return
    this.closed = true
    this.worker.terminate()
    this.pending.clear()
  }

  private failAll(error: Error) {
    this.pending.forEach(({ reject }) => reject(error))
    this.pending.clear()
    this.dispose()
  }

  private call(
    action: OpfsWriterRequest['action'],
    payload?: unknown,
    transferables: Transferable[] = []
  ) {
    if (this.closed) {
      return Promise.reject(new Error('OPFS writer already closed'))
    }
    const id = ++this.nextId
    return new Promise<unknown>((resolve, reject) => {
      this.pending.set(id, { resolve, reject })
      const message = { id, action, payload } as OpfsWriterRequest
      this.worker.postMessage(message, transferables)
    })
  }
}

async function writeFileViaWorker(fileName: string, contents: Uint8Array) {
  const writer = await OpfsWriterClient.create(fileName)
  try {
    await writer.write(contents)
    await writer.close()
  } catch (error) {
    await writer.abort().catch(() => {})
    throw error
  }
}

let promiser: SqlitePromiser | null = null
let isInitializing = false

const MANIFEST_URL = '/dict-manifest.json'
const DB_FILENAME = 'dictionary.sqlite'
const META_FILENAME = 'dictionary.meta.json'
const VERSION_KEY = 'dict_version'
const TIER_KEY = 'dict_tier'
const FORCE_DOWNLOAD_KEY = 'dict_force_download'
const AUTO_UPDATE_KEY = 'offline-auto-update'

export type DictTier = 'free' | 'premium'

interface TierInfo {
  file: string
  entryCount: number
  sizeBytes: number
  checksum: string
}

interface DictManifest {
  version: string
  generatedAt?: string
  baseUrl: string | null // R2 public URL or null for local
  tiers: {
    premium: TierInfo
  }
  locales?: Record<string, { version: string }>
  // Legacy support
  file?: string
}

export interface OfflineDbFileInfo {
  size: number
  lastModified: number
  version: string | null
  tier: DictTier | null
  entryCount: number | null
}

let manifestCache: DictManifest | null = null
let manifestPromise: Promise<DictManifest> | null = null

// --- HELPERS ---
async function fetchManifest(force = false): Promise<DictManifest> {
  if (manifestCache && !force) {
    return manifestCache
  }
  if (manifestPromise && !force) {
    return manifestPromise
  }

  manifestPromise = (async () => {
    try {
      const res = await fetch(MANIFEST_URL, { cache: 'no-cache' })
      if (!res.ok) throw new Error(`Manifest 404: ${res.statusText}`)
      const payload = (await res.json()) as DictManifest
      manifestCache = payload
      return payload
    } finally {
      manifestPromise = null
    }
  })()

  return manifestPromise
}

export async function getLatestManifest(force = false) {
  try {
    const manifest = await fetchManifest(force)
    return { manifest, error: null as Error | null }
  } catch (error) {
    return { manifest: manifestCache, error: error as Error }
  }
}

interface MetadataContent {
  version?: string
  tier?: DictTier
  entryCount?: number
}

const metadataFile = {
  async read(root: FileSystemDirectoryHandle): Promise<MetadataContent> {
    try {
      const handle = await root.getFileHandle(META_FILENAME)
      const file = await handle.getFile()
      return JSON.parse(await file.text()) as MetadataContent
    } catch {
      return {}
    }
  },
  async write(root: FileSystemDirectoryHandle, data: MetadataContent) {
    const payload = JSON.stringify(data, null, 2)
    try {
      const bytes = new TextEncoder().encode(payload)
      await writeFileViaWorker(META_FILENAME, bytes)
      return
    } catch (error) {
      console.warn('Falling back to direct metadata write', error)
    }

    const handle = await root.getFileHandle(META_FILENAME, { create: true })
    const writable = await handle.createWritable()
    await writable.write(payload)
    await writable.close()
  },
  async clear(root: FileSystemDirectoryHandle) {
    try {
      await root.removeEntry(META_FILENAME)
    } catch {}
  }
}

const offlineState = {
  async getVersion(root: FileSystemDirectoryHandle) {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(VERSION_KEY)
      if (stored) return stored
    }
    const meta = await metadataFile.read(root)
    if (meta.version) {
      if (typeof window !== 'undefined') {
        localStorage.setItem(VERSION_KEY, meta.version)
      }
      return meta.version
    }
    return null
  },
  async getTier(root: FileSystemDirectoryHandle): Promise<DictTier | null> {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(TIER_KEY) as DictTier | null
      if (stored) return stored
    }
    const meta = await metadataFile.read(root)
    if (meta.tier) {
      if (typeof window !== 'undefined') {
        localStorage.setItem(TIER_KEY, meta.tier)
      }
      return meta.tier
    }
    return null
  },
  async setVersionAndTier(
    root: FileSystemDirectoryHandle,
    version: string,
    tier: DictTier,
    entryCount?: number
  ) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(VERSION_KEY, version)
      localStorage.setItem(TIER_KEY, tier)
    }
    await metadataFile.write(root, { version, tier, entryCount })
  },
  async clearVersion(root: FileSystemDirectoryHandle) {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(VERSION_KEY)
      localStorage.removeItem(TIER_KEY)
    }
    await metadataFile.clear(root)
  },
  shouldForceDownload() {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(FORCE_DOWNLOAD_KEY) === 'true'
  },
  requestForceDownload() {
    if (typeof window !== 'undefined') {
      localStorage.setItem(FORCE_DOWNLOAD_KEY, 'true')
    }
  },
  clearForceFlag() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(FORCE_DOWNLOAD_KEY)
    }
  }
}

export const requestForceDownload = () => offlineState.requestForceDownload()

export async function getLocalDbFile(): Promise<OfflineDbFileInfo | null> {
  if (typeof window === 'undefined') return null
  try {
    const opfsRoot = await navigator.storage.getDirectory()
    const handle = await opfsRoot.getFileHandle(DB_FILENAME)
    const file = await handle.getFile()
    const meta = await metadataFile.read(opfsRoot)
    return {
      size: file.size,
      lastModified: file.lastModified,
      version: meta.version ?? localStorage.getItem(VERSION_KEY),
      tier:
        (meta.tier as DictTier) ??
        (localStorage.getItem(TIER_KEY) as DictTier | null),
      entryCount: meta.entryCount ?? null
    }
  } catch {
    return null
  }
}

// --- 1. DOWNLOAD LOGIC ---
async function prepareDatabaseFile(onProgress?: (percent: number) => void) {
  const tier: DictTier = 'premium'
  if (typeof window === 'undefined') {
    throw new Error('prepareDatabaseFile can only be called in the browser')
  }
  const opfsRoot = await navigator.storage.getDirectory()
  const storedVersion = await offlineState.getVersion(opfsRoot)
  const storedTier = await offlineState.getTier(opfsRoot)
  const autoUpdateEnabled = localStorage.getItem(AUTO_UPDATE_KEY) !== 'false'
  const forceDownload = offlineState.shouldForceDownload()

  // Check if tier changed (need to re-download)
  const tierChanged = storedTier !== tier

  // 1. Check if we already have a file on disk
  let fileExists = false
  try {
    await opfsRoot.getFileHandle(DB_FILENAME)
    fileExists = true
  } catch {
    fileExists = false
  }

  // 2. Try to fetch Manifest (Update Check)
  let manifest: DictManifest | undefined
  let networkError = false

  try {
    manifest = await fetchManifest()
  } catch {
    console.warn('Network check failed (Offline?).')
    networkError = true
  }

  // 3. Handle Offline Scenario
  if (networkError) {
    if (fileExists && !tierChanged) {
      console.log(
        `Offline mode detected. Using local dictionary (v${storedVersion || 'unknown'}, ${storedTier || 'unknown'} tier).`
      )
      if (onProgress) onProgress(100)
      return
    } else {
      throw new Error('Cannot download dictionary: No internet connection.')
    }
  }

  // 4. Get tier info from manifest
  const tierInfo = manifest!.tiers.premium
  if (!tierInfo) {
    throw new Error('Complete dictionary is not available in manifest')
  }

  // 5. Version and tier check
  const upToDate = storedVersion === manifest!.version && !tierChanged
  if (fileExists && !forceDownload && !tierChanged) {
    const blocked = !autoUpdateEnabled && !upToDate
    if (upToDate || blocked) {
      console.log(
        upToDate
          ? `Dictionary v${manifest!.version} (${tier}) is up to date.`
          : `Auto update disabled. Keeping local dictionary v${storedVersion || 'unknown'}.`
      )
      if (onProgress) onProgress(100)
      return
    }
  }

  // 6. Build download URL
  const fileName = tierInfo.file
  const baseUrl = manifest!.baseUrl || ''
  const downloadUrl = baseUrl ? `${baseUrl}/${fileName}` : `/${fileName}`

  console.log(`Downloading ${fileName} from ${downloadUrl}...`)
  if (tierChanged) {
    console.log(`Tier change: ${storedTier} → ${tier}`)
  }

  const response = await fetch(downloadUrl)
  if (!response.ok) {
    throw new Error(
      `Download failed: ${response.status} ${response.statusText}`
    )
  }

  const total = parseInt(response.headers.get('Content-Length') || '0', 10)
  let loaded = 0

  if (!response.body) {
    throw new Error('Streaming downloads are not supported in this browser.')
  }

  const reader = response.body.getReader()
  const writerClient = await OpfsWriterClient.create(DB_FILENAME)

  let decompressionStream: DecompressionStream | null = null
  let decompressionWriter: WritableStreamDefaultWriter<BufferSource> | null =
    null
  let decompressedReader: ReadableStreamDefaultReader<Uint8Array> | null = null
  let decompressedPump: Promise<void> | null = null

  const reportProgress = () => {
    if (onProgress && total) {
      const pct = Math.min((loaded / total) * 100, 100)
      onProgress(pct)
    }
  }

  try {
    const { value: firstChunk, done } = await reader.read()
    if (done || !firstChunk) throw new Error('Empty file received')

    const isGzipped = firstChunk[0] === 0x1f && firstChunk[1] === 0x8b
    const headerStr = new TextDecoder().decode(firstChunk.slice(0, 15))

    if (headerStr.includes('<!DOCTYPE') || headerStr.includes('<html')) {
      throw new Error('Server returned HTML (404?) instead of binary file.')
    }

    console.log(
      `File header detected: ${isGzipped ? 'GZIP' : 'RAW/Already Decompressed'}`
    )

    if (isGzipped) {
      decompressionStream = new DecompressionStream('gzip')
      decompressionWriter = decompressionStream.writable.getWriter()
      decompressedReader = decompressionStream.readable.getReader()

      decompressedPump = (async () => {
        while (true) {
          const { value, done } = await decompressedReader!.read()
          if (value && value.byteLength) {
            await writerClient.write(value)
          }
          if (done) break
        }
      })()

      if (!decompressionWriter) {
        throw new Error('Failed to initialize decompression writer')
      }

      await decompressionWriter.write(firstChunk)
      loaded += firstChunk.byteLength
      reportProgress()

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        if (value) {
          loaded += value.byteLength
          reportProgress()
          await decompressionWriter.write(value)
        }
      }

      await decompressionWriter.close()
      if (decompressedPump) {
        await decompressedPump
      }
    } else {
      await writerClient.write(firstChunk)
      loaded += firstChunk.byteLength
      reportProgress()

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        if (value) {
          loaded += value.byteLength
          reportProgress()
          await writerClient.write(value)
        }
      }
    }

    await offlineState.setVersionAndTier(
      opfsRoot,
      manifest!.version,
      tier,
      tierInfo?.entryCount
    )
    if (forceDownload) {
      offlineState.clearForceFlag()
    }
    console.log(
      `Dictionary v${manifest!.version} (${tier} tier) saved to OPFS.`
    )
  } catch (e) {
    try {
      if (decompressionWriter) await decompressionWriter.close()
    } catch {}
    try {
      if (decompressedReader) await decompressedReader.cancel()
    } catch {}
    await writerClient.abort().catch(() => {})
    throw e
  } finally {
    await writerClient.close().catch(() => {})
  }
}

// --- 2. INITIALIZATION (FIXED) ---
export async function initDB(
  onProgress?: (percent: number) => void
): Promise<SqlitePromiser> {
  const forceDownload = offlineState.shouldForceDownload()

  if (forceDownload && promiser) {
    try {
      await promiser('close', {})
    } catch {}
    promiser = null
  }

  if (promiser) return promiser
  if (isInitializing) {
    while (isInitializing) await new Promise((r) => setTimeout(r, 100))
    if (promiser) return promiser
    throw new Error('SQLite initialization did not complete')
  }

  isInitializing = true

  try {
    await prepareDatabaseFile(onProgress)

    console.log('Initializing Worker...')

    // Wait for sqlite3Worker1Promiser to be available
    if (typeof window !== 'undefined') {
      let attempts = 0
      while (!window.sqlite3Worker1Promiser && attempts < 50) {
        await new Promise((r) => setTimeout(r, 100))
        attempts++
      }
      if (!window.sqlite3Worker1Promiser) {
        throw new Error('sqlite3Worker1Promiser failed to load')
      }
    }

    // --- FIX START: Added explicit error handling to the Promise ---
    const _promiser = await new Promise<SqlitePromiser>((resolve, reject) => {
      try {
        const promiserFn = getSqlite3Worker1Promiser()
        const p = promiserFn({
          onready: () => resolve(p),
          // Point to the static file we copied
          worker: () => new Worker('/sqlite/sqlite3-worker1.js?isolation=2'),
          // Capture worker startup errors
          onerror: (err: unknown) => {
            console.error('SQLite Worker Error:', err)
            reject(new Error(`Worker failed to start: ${JSON.stringify(err)}`))
          }
        })
      } catch (err) {
        reject(err)
      }
    })
    // --- FIX END ---

    console.log('Worker created, opening DB...')
    const openResponse = await _promiser('open', {
      filename: `file:${DB_FILENAME}?vfs=opfs`
    })

    if (openResponse.type === 'error') {
      console.error('OPFS Open Error:', openResponse)
      throw new Error(
        openResponse.result?.message ?? 'Failed to open SQLite database'
      )
    }

    console.log('SQLite Worker Ready.')
    promiser = _promiser
    return promiser
  } catch (e) {
    console.error('Init Failed:', e)
    throw e
  } finally {
    isInitializing = false
  }
}

// --- 3. CLOSE (release connection without deleting) ---
export async function closeDatabase() {
  if (promiser) {
    try {
      await promiser('close', {})
      console.log('SQLite connection closed.')
    } catch {}
    promiser = null
  }
}

// --- 4. DELETE ---
export async function deleteDatabase() {
  await closeDatabase()
  try {
    const opfsRoot = await navigator.storage.getDirectory()
    await opfsRoot.removeEntry(DB_FILENAME)
    await offlineState.clearVersion(opfsRoot)
    console.log('Deleted.')
    return true
  } catch {
    return false
  }
}

// --- 4. QUERY ---
export async function runQuery<T extends SqliteRow = SqliteRow>(
  sql: string,
  params: SqliteBindValue[] = []
): Promise<T[]> {
  const p = await initDB()
  const response = await p('exec', {
    sql,
    bind: params,
    returnValue: 'resultRows',
    rowMode: 'object'
  })

  if (response.type === 'error') {
    console.error('SQLite Query Error:', response.result)
    throw new Error(response.result?.message ?? 'SQLite query failed')
  }

  const rows = response.result?.resultRows ?? []

  return rows.map((row) => {
    const next = { ...row }
    if (typeof next.json_data === 'string') {
      try {
        next.json_data = JSON.parse(next.json_data) as unknown
      } catch {}
    }
    return next as T
  })
}

export async function checkDbExists(): Promise<boolean> {
  try {
    const opfsRoot = await navigator.storage.getDirectory()
    await opfsRoot.getFileHandle(DB_FILENAME)
    return true
  } catch {
    return false
  }
}
